import { create } from "zustand";

import createSelectors from "./create-selectors";

const TOAST_TIMEOUT = 7000; // 1000 = 1 second
export const useToastStoreBase = create<ToastState>()((set, get) => ({
  lastId: 0,
  toasts: [],
  add: (toast, opts) => {
    const ids = get().addMulti([toast], opts);
    return ids?.[0];
  },
  addMulti: (toasts, opts) => {
    if (toasts.length === 0) return;
    const ids: Toast['id'][] = [];
    const options = Object.assign(DEFAULT_ADD_OPTIONS, opts) as AddOptions;
    let lastId = get().lastId;
    const newToasts = toasts.map((t) => {
      const id = lastId++;
      ids.push(id);
      let timeoutId: NodeJS.Timeout | null = null;
      if (options.timeout !== "off" && options.timeout > 0) {
        timeoutId = setTimeout(() => get().remove(id), options.timeout);
      }
      return { ...t, id, timeoutId };
    });
    set((state) => ({
      toasts: [...state.toasts, ...newToasts],
      lastId: lastId,
    }));
    return ids;
  },
  remove: (id, opts) => {
    const options = Object.assign(DEFAULT_REMOVE_OPTIONS, opts) as RemoveOptions;
    const toast = get().toasts.find((t) => t.id === id);
    if (!toast) return;
    const { timeoutId, onClose } = toast;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (onClose && !options.disableCallback) {
      onClose();
    }
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
}));

/**
 * 'success-solid' is a variant that emulate the old style of toasts we have in networx-code resources
 */
export type ToastVariant = "success" | "error" | "warn" | "no-icon" | "success-solid";
type Toast = {
  id: number;
  title: string;
  description?: string;
  htmlContent?: React.ReactNode;
  onClose?: () => void;
  variant?: ToastVariant;
  timeoutId: NodeJS.Timeout | null;
};

type AddOptions = { timeout: number | "off" };
const DEFAULT_ADD_OPTIONS: AddOptions = { timeout: TOAST_TIMEOUT };
type RemoveOptions = { disableCallback: boolean }
const DEFAULT_REMOVE_OPTIONS: RemoveOptions = { disableCallback: false };
interface ToastState {
  lastId: number;
  toasts: Toast[];
  /**
   * @param {Object} opts
   * @param {number} opts.timeout - milliseconds. if you pass 0 or "off" to timeout, the toast will not auto-dismiss */
  add: (toast: Omit<Toast, "id" | "timeoutId">, opts?: Partial<AddOptions>) => Toast['id'] | undefined
  addMulti: (toasts: Array<Omit<Toast, "id" | "timeoutId">>, opts?: Partial<AddOptions>) => Toast['id'][] | undefined;
  remove: (id: number, opts?: Partial<RemoveOptions>) => void;
}
const useToastStore = createSelectors(useToastStoreBase);
export default useToastStore;
